import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * IDM placeholder payloads are not uniformly strings (`minimumAge` is a
 * number), so values are coerced at substitution time.
 */
type PlaceholderValues = Record<string, string | number>;

export type Translate = (
	key: string,
	values?: PlaceholderValues,
	fallback?: string
) => string;

export type { PlaceholderValues };

const PLACEHOLDER_PATTERN = /{(\w+)}/g;

export const useAppTranslation = (placeholders?: PlaceholderValues | null) => {
	const { t: rawT, i18n } = useTranslation();

	// Read through a ref so `t` stays referentially stable even when callers
	// pass a fresh object literal (`placeholders ?? {}`) on every render, while
	// still formatting against the latest values.
	const placeholdersRef = useRef(placeholders);
	placeholdersRef.current = placeholders;

	// `t` is memoised because consumers pass it as a prop and list it in
	// dependency arrays; a new closure per render defeated every memo
	// downstream of it.
	const t = useCallback<Translate>(
		(key, values, fallback) => {
			const text = rawT(key, {
				defaultValue: fallback,
				interpolation: { escapeValue: false },
			});
			const textStr =
				typeof text === 'string' ? text : String(text ?? '');

			return textStr.replaceAll(PLACEHOLDER_PATTERN, (_, placeholder) => {
				const value =
					values?.[placeholder] ??
					placeholdersRef.current?.[placeholder];

				return value === undefined ? `{${placeholder}}` : String(value);
			});
		},
		[rawT]
	);

	return useMemo(() => ({ t, i18n }), [t, i18n]);
};
